import { useState, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { useErrorStore } from '#lib/stores/errorStore';

import { transitionStage as transitionStageApi } from '../../../api/crmApi';
import { dealsKeys } from '../../../models/deals/keys';

import { compareByNextActivityDueAt } from './dealOrdering';

/**
 * Optimistically move a deal between stage caches: drop it from the source
 * stage's pages and insert it into the target stage's first page at its
 * due-date-sorted position, so it appears in the right spot until the refetch
 * confirms the server order.
 */
function moveDealAcrossCaches(qc, { pipelineId, dealId, sourceStageId, targetStageId, movedDeal, filters }) {
	const sourceQueryKey = dealsKeys.byStageInfinite(pipelineId, sourceStageId, filters);
	qc.setQueryData(sourceQueryKey, (old) => {
		if (!old?.pages) return old;
		return {
			...old,
			pages: old.pages.map((page) => ({
				...page,
				deals: page.deals.filter((d) => d.id !== dealId),
			})),
		};
	});

	if (!movedDeal) return;
	const targetQueryKey = dealsKeys.byStageInfinite(pipelineId, targetStageId, filters);
	qc.setQueryData(targetQueryKey, (old) => {
		if (!old?.pages) return old;
		const alreadyInTarget = old.pages.some((p) => p.deals.some((d) => d.id === dealId));
		if (alreadyInTarget) return old;
		const updatedDeal = { ...movedDeal, stageId: targetStageId };
		return {
			...old,
			pages: old.pages.map((page, i) =>
				i === 0 ? { ...page, deals: [...page.deals, updatedDeal].sort(compareByNextActivityDueAt) } : page,
			),
		};
	});
}

/**
 * Overwrite the optimistically-placed card with the deal the server returned.
 *
 * The optimistic copy only knows the new `stageId` — `status`, `lossReason` and
 * `closingChance` are all derived server-side from the target stage's type. Left
 * unreconciled, the card renders a stale status until the invalidated query
 * refetches: a just-lost deal shows no loss reason, and a lost-then-won deal
 * keeps showing the old one.
 */
function applyServerDeal(qc, { pipelineId, targetStageId, filters, serverDeal }) {
	if (!serverDeal?.id) return;
	qc.setQueryData(dealsKeys.byStageInfinite(pipelineId, targetStageId, filters), (old) => {
		if (!old?.pages) return old;
		return {
			...old,
			pages: old.pages.map((page) => ({
				...page,
				deals: page.deals.map((d) => (d.id === serverDeal.id ? { ...d, ...serverDeal } : d)),
			})),
		};
	});
}

export function useDealDragDrop({ pipelineId, filters, stageDealsRef, stages = [] }) {
	const [activeDeal, setActiveDeal] = useState(null);
	// Pending drop onto a lost-type stage, awaiting the loss-reason dialog.
	const [lostPrompt, setLostPrompt] = useState(null);
	const [lostSubmitting, setLostSubmitting] = useState(false);
	const originalStageRef = useRef(null);
	const qc = useQueryClient();
	const showError = useErrorStore((s) => s.showError);

	// Optimistically move the card, persist the transition, and reconcile caches.
	// Shared by the immediate path and the lost-stage dialog confirmation.
	const commitTransition = ({
		dealId,
		sourceStageId,
		targetStageId,
		movedDeal,
		lossReason = null,
		lossReasonNote = null,
	}) => {
		moveDealAcrossCaches(qc, { pipelineId, dealId, sourceStageId, targetStageId, movedDeal, filters });

		const syncCache = () => {
			qc.invalidateQueries({ queryKey: dealsKeys.byStageInfinite(pipelineId, targetStageId) });
			qc.invalidateQueries({ queryKey: dealsKeys.byStageInfinite(pipelineId, sourceStageId) });
			qc.invalidateQueries({ queryKey: dealsKeys.stageSummaries(pipelineId) });
			// A won/lost target flips the deal's status (and closing chance) — refresh
			// the deal detail and its company page so the closed-deal banner there
			// doesn't show stale data until a manual reload.
			qc.invalidateQueries({ queryKey: dealsKeys.detail(dealId) });
			// Every transition writes a stage_change activity (carrying the loss
			// reason), so the company page's timeline is stale too.
			qc.invalidateQueries({ queryKey: ['crm', 'activities'] });
			if (movedDeal?.crmCompanyId) {
				qc.invalidateQueries({ queryKey: ['crm', 'company', movedDeal.crmCompanyId] });
			}
		};

		return transitionStageApi(dealId, {
			stageId: targetStageId,
			...(lossReason !== null ? { lossReason } : {}),
			...(lossReasonNote !== null ? { lossReasonNote } : {}),
		})
			.then((res) => {
				// Reconcile before invalidating: the refetch is async, and until it
				// lands the card would otherwise render the optimistic copy's stale
				// status / loss reason.
				applyServerDeal(qc, { pipelineId, targetStageId, filters, serverDeal: res?.deal });
				syncCache();
			})
			.catch((err) => {
				showError({ error: err, title: 'Stage konnte nicht gewechselt werden' });
				syncCache();
				// The server rejects an invalid or missing loss reason. Rethrow so the
				// lost-stage dialog stays open on the user's input instead of closing
				// over a transition that never happened.
				throw err;
			});
	};

	const handleDragStart = (event) => {
		let deal = null;
		for (const data of stageDealsRef.current.values()) {
			deal = data.dealMap.get(event.active.id);
			if (deal) break;
		}
		if (deal) {
			setActiveDeal(deal);
			originalStageRef.current = deal.stageId;
		}
	};

	const handleDragCancel = () => {
		setActiveDeal(null);
		originalStageRef.current = null;
	};

	const handleDragEnd = (event) => {
		setActiveDeal(null);
		const { active, over } = event;
		const sourceStageId = originalStageRef.current;
		originalStageRef.current = null;

		if (!over || !sourceStageId) return;

		const activeId = active.id;
		// Only stage columns are drop targets (see kanbanCollision).
		const targetStageId = over.data.current?.stageId;

		// Same stage (or no resolvable target): nothing to persist — the card
		// settles back into its due-date-sorted position on the next render.
		if (!targetStageId || targetStageId === sourceStageId) return;

		const movedDeal = stageDealsRef.current.get(sourceStageId)?.dealMap.get(activeId);

		// Dropping onto a lost stage opens the loss-reason dialog first; the move
		// is deferred until the user confirms. We don't move the card yet, so
		// cancelling simply leaves it in its original column.
		const targetStage = stages.find((s) => s.id === targetStageId);
		if (targetStage?.stageType === 'lost') {
			setLostPrompt({ dealId: activeId, sourceStageId, targetStageId, movedDeal });
			return;
		}

		// Already surfaced by commitTransition's error toast; nothing left to do here.
		commitTransition({ dealId: activeId, sourceStageId, targetStageId, movedDeal }).catch(() => {});
	};

	// Closes the dialog only once the transition actually persisted — a rejected
	// loss reason leaves it open so the user can correct their input.
	const confirmLostTransition = async ({ lossReason, lossReasonNote }) => {
		if (!lostPrompt) return;
		setLostSubmitting(true);
		try {
			await commitTransition({ ...lostPrompt, lossReason, lossReasonNote });
			setLostPrompt(null);
		} catch {
			// Error already surfaced by commitTransition; keep the dialog open.
		} finally {
			setLostSubmitting(false);
		}
	};

	const cancelLostTransition = () => setLostPrompt(null);

	return {
		activeDeal,
		handleDragStart,
		handleDragEnd,
		handleDragCancel,
		lostPrompt,
		lostSubmitting,
		confirmLostTransition,
		cancelLostTransition,
	};
}
